import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { InlineCalendar } from '@/modules/common/components/molecules/date/date-range-picker-v2/components/inline-calendar';
import { PresetCategoriesList } from '@/modules/common/components/molecules/date/date-range-picker-v2/components/preset-category-item';
import {
  DateRangePreset,
  DateRangeV2,
} from '@/modules/common/components/molecules/date/date-range-picker-v2/types';
import {
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2/utils';

import { DateRangeFilterConfig } from '../types/types';
import {
  parseDateRangeFilterValue,
  serializeDateRangeFilterValue,
} from '../utils/date-range-filter-value';

interface DateRangeFilterSubmenuProps {
  config: DateRangeFilterConfig;
  activeValues: string[];
  onFilterChange: (name: string, value: string[]) => void;
  onClearFilter: (name: string) => void;
  onCloseSubmenu?: () => void;
}

/**
 * Submenu for `dateRange` filters: the DateRangePickerV2 content block
 * (preset categories + inline calendar) without its trigger/dropdown shell.
 * The value is stored as a single 'yyyy-MM-dd:yyyy-MM-dd' string.
 */
export function DateRangeFilterSubmenu({
  config,
  activeValues,
  onFilterChange,
  onClearFilter,
  onCloseSubmenu,
}: DateRangeFilterSubmenuProps) {
  const parsed = parseDateRangeFilterValue(activeValues[0]);
  const selectedPreset = parsed ? detectPresetFromDates(parsed) : null;

  // Selection keeps the submenu open (same as toggling a checkbox in the
  // multiselect submenus); only Reset closes it, matching "Clear all".
  const applyRange = (range: DateRangeV2) => {
    onFilterChange(config.name, [serializeDateRangeFilterValue(range)]);
  };

  const handlePresetSelect = (preset: DateRangePreset) => {
    applyRange(getDateRangeFromPreset(preset));
  };

  const handleCustomChange = (range: DateRangeV2) => {
    applyRange(range);
  };

  const handleReset = () => {
    onClearFilter(config.name);
    onCloseSubmenu?.();
  };

  return (
    <>
      <div className="p-1">
        <PresetCategoriesList
          selectedPreset={selectedPreset}
          onSelect={handlePresetSelect}
          hiddenCategories={config.hiddenPresetCategories}
          keepMenuOpenOnSelect
        />
      </div>

      <DropdownMenuSeparator className="my-0" />

      <InlineCalendar dates={parsed} onChange={handleCustomChange} onReset={handleReset} />
    </>
  );
}
